import { CLAIM_STATUS, RESOLUTION_TYPE, RETURN_STAGE } from "../constants";
import type { Claim, ClaimTranslator, HeroContent } from "../types";
import { getEffectiveResolution } from "./claimState";
import type { ReturnStage } from "./returnState";
import { getReturnStage, isReturnRequest, requestTerm } from "./returnState";

const getHeroKey = (claim: Claim): string => {
  if (claim.claim_status === CLAIM_STATUS.DENIED) {
    return CLAIM_STATUS.DENIED;
  }
  if (
    claim.claim_status === CLAIM_STATUS.NEW ||
    claim.claim_status === CLAIM_STATUS.PENDING
  ) {
    return claim.claim_status;
  }

  if (claim.claim_status === CLAIM_STATUS.CLOSED) {
    return CLAIM_STATUS.CLOSED;
  }

  const resolutionType = getEffectiveResolution(claim);
  if (
    resolutionType === RESOLUTION_TYPE.REFUND &&
    claim.resolution?.refunds?.length
  ) {
    return "refundIssued";
  }
  if (
    resolutionType === RESOLUTION_TYPE.STORE_CREDIT &&
    claim.resolution?.store_credits?.length
  ) {
    return "storeCreditApplied";
  }
  if (resolutionType === RESOLUTION_TYPE.RESHIP) {
    const shipment = claim.resolution?.reships?.[0]?.shipments?.[0];
    if (shipment?.delivered_at) {
      return "reshipDelivered";
    }
    if (shipment?.tracking_number) {
      return "reshipShipped";
    }
    return "reshipPending";
  }

  if (claim.claim_status === CLAIM_STATUS.APPROVED) {
    return CLAIM_STATUS.APPROVED;
  }

  return claim.claim_status;
};

const getReturnHeroKey = (claim: Claim, stage: ReturnStage): string =>
  stage === RETURN_STAGE.REQUESTED ? claim.claim_status : stage;

const getReturnHeroContent = (
  claim: Claim,
  t: ClaimTranslator,
): HeroContent => {
  const stage = getReturnStage(claim);
  const prefix = `status.returns.${claim.request_type}.${stage}`;
  return {
    key: getReturnHeroKey(claim, stage),
    title: t(`${prefix}.title`),
    description: t(`${prefix}.description`),
    statusLabel: t(`${prefix}.statusLabel`),
  };
};

export const getHeroContent = (
  claim: Claim,
  t: ClaimTranslator,
): HeroContent => {
  if (isReturnRequest(claim)) {
    return getReturnHeroContent(claim, t);
  }
  const heroKey = getHeroKey(claim);
  return {
    key: heroKey,
    title: t(`status.hero.${heroKey}.title`),
    description: t(`status.hero.${heroKey}.description`),
    statusLabel: t(`status.hero.${heroKey}.statusLabel`),
  };
};

export const getStatusAnnouncement = (
  claim: Claim,
  hero: HeroContent,
  t: ClaimTranslator,
) =>
  t("status.announcement", {
    status: hero.statusLabel,
    claimNumber: claim.claim_number,
    requestType: requestTerm(claim),
  });
